import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import config from '../config'
import keycloak from '../keycloak'

const ApiClient = axios.create({
  baseURL: `${config.apiUrl}staffapi/`,
  withCredentials: true,
})

type RetryableRequestConfig = InternalAxiosRequestConfig & { authRetried?: boolean }

let tokenRefresh: Promise<boolean> | undefined

const refreshAccessToken = async (minValidity: number) => {
  if (!keycloak.authenticated) throw new Error('Please sign in again.')

  // An authentication failure needs a forced refresh, even if a proactive
  // validity check is currently pending.
  if (minValidity === -1 && tokenRefresh) await tokenRefresh

  if (!tokenRefresh) {
    tokenRefresh = keycloak.updateToken(minValidity).finally(() => {
      tokenRefresh = undefined
    })
  }
  await tokenRefresh
  if (!keycloak.token) throw new Error('Please sign in again.')
}

ApiClient.interceptors.request.use(async request => {
  if (keycloak.authenticated) await refreshAccessToken(60)

  request.headers.set('Accept-Language', localStorage.getItem('lang') || 'uk')
  if (keycloak.token) {
    request.headers.set('Authorization', `Bearer ${keycloak.token}`)
  } else {
    request.headers.delete('Authorization')
  }
  return request
})

ApiClient.interceptors.response.use(response => response, async (error: AxiosError) => {
  const request = error.config as RetryableRequestConfig | undefined
  const status = error.response?.status

  if ((status === 401 || status === 498) && request && !request.authRetried && keycloak.authenticated) {
    request.authRetried = true
    await refreshAccessToken(-1)
    return ApiClient.request(request)
  }

  return Promise.reject(error)
})

export default ApiClient
